import React, { useState, useEffect } from "react";
import { authLogin, authMe, authLogout, getStats } from "../api";
import { useToast } from "../hooks/use-toast";
import { LayoutDashboard, FileText, MessageSquare, Users, CalendarClock,
  LogOut, Lock, TrendingUp, Columns3, Globe, Mail, BarChart3, Share2, User, KeyRound, Rocket, Settings, MapPin, Inbox, Repeat, CalendarOff, Briefcase, Building2, Cloud, ShieldCheck, PanelsTopLeft,
} from "lucide-react";
import { QuotesPanel, MessagesPanel } from "../components/admin/Quotes";
import WeeklyRecap from "../components/admin/WeeklyRecap";
import ProspectsPanel from "../components/admin/Prospects";
import AppointmentsPanel from "../components/admin/Appointments";
import PipelinePanel from "../components/admin/Pipeline";
import LeadsPanel from "../components/admin/Leads";
import CampaignsPanel from "../components/admin/Campaigns";
import AnalyticsPanel from "../components/admin/Analytics";
import SocialPanel from "../components/admin/Social";
import TeamPanel from "../components/admin/Team";
import AutopilotPanel from "../components/admin/Autopilot";
import EmailSettingsPanel from "../components/admin/EmailSettings";
import InterventionsPanel from "../components/admin/Interventions";
import MailboxPanel from "../components/admin/Mailbox";
import ContractsPanel from "../components/admin/Contracts";
import AbsencesPanel from "../components/admin/Absences";
import RecruitmentPanel from "../components/admin/Recruitment";
import TiersPanel from "../components/admin/Tiers";
import DolibarrSyncPanel from "../components/admin/DolibarrSync";
import SitePanel from "../components/admin/SitePanel";
import QualityPanel from "../components/admin/Quality";
import { log } from "../lib/log";

const Login = ({ onLogin }) => {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await authLogin(login, password);
      sessionStorage.setItem("crm_login", res.data.login || login);
      sessionStorage.setItem("crm_role", res.data.role || "");
      if (res.data.warning) toast({ title: "Connexion en mode dégradé", description: res.data.warning, variant: "destructive" });
      onLogin(res.data.role || "");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Identifiant ou mot de passe incorrect";
      toast({ title: "Accès refusé", description: detail, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 flex items-center justify-center px-5">
      <form onSubmit={submit} className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 w-full max-w-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-brand text-white rounded-lg p-2.5"><Lock size={20} /></div>
          <div>
            <h1 className="font-heading font-bold text-white text-lg leading-none">Espace CRM</h1>
            <p className="text-xs text-neutral-400 mt-1">PTITSERVICES-PRO</p>
          </div>
        </div>
        <label className="block text-xs font-semibold text-neutral-400 mb-1.5">Identifiant Dolibarr</label>
        <input
          type="text"
          value={login}
          onChange={(e) => setLogin(e.target.value)}
          className="w-full bg-neutral-800 border border-neutral-700 rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:border-brand mb-4"
          placeholder="votre identifiant"
          autoFocus
        />
        <label className="block text-xs font-semibold text-neutral-400 mb-1.5">Clé API Dolibarr (ou mot de passe admin)</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-neutral-800 border border-neutral-700 rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:border-brand"
          placeholder="••••••••"
        />
        <button type="submit" disabled={loading} className="mt-5 w-full bg-brand hover:bg-brand-dark text-white font-semibold py-3 rounded-md disabled:opacity-60">
          {loading ? "Connexion..." : "Se connecter"}
        </button>
        <p className="mt-4 text-xs text-neutral-500 text-center">
          Connectez-vous avec votre clé API Dolibarr personnelle.
        </p>
      </form>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-5">
    <div className="flex items-center justify-between">
      <p className="text-sm text-neutral-500">{label}</p>
      <Icon size={18} className={accent || "text-brand"} />
    </div>
    <p className="font-heading font-extrabold text-3xl text-neutral-900 mt-2">{value}</p>
  </div>
);

const Overview = () => {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
     
    getStats().then((r) => setStats(r.data)).catch((e) => setError(e?.response?.data?.detail || "Impossible de charger les statistiques"));
  }, []);
  if (error) return <p className="text-red-600 text-sm" data-testid="overview-error">{error}</p>;
  if (!stats) return <p className="text-neutral-500">Chargement...</p>;
  return (
    <div>
      <h2 className="font-heading font-bold text-xl text-neutral-900 mb-5">Vue d'ensemble</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={FileText} label="Demandes de devis" value={stats.quotes} />
        <StatCard icon={TrendingUp} label="Devis nouveaux" value={stats.quotes_new} accent="text-amber-500" />
        <StatCard icon={MessageSquare} label="Messages" value={stats.messages} />
        <StatCard icon={Users} label="Prospects" value={stats.prospects} />
        <StatCard icon={Users} label="Prospects convertis" value={stats.prospects_converted} accent="text-emerald-500" />
        <StatCard icon={CalendarClock} label="Rendez-vous" value={stats.appointments} />
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${stats.dolibarr_configured ? "bg-emerald-100 text-emerald-700" : "bg-neutral-200 text-neutral-600"}`}>
          Dolibarr {stats.dolibarr_configured ? "connecté ✓" : "non configuré"}
        </span>
        <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${stats.smtp_configured ? "bg-emerald-100 text-emerald-700" : "bg-neutral-200 text-neutral-600"}`}>
          Email notifications {stats.smtp_configured ? "connecté ✓" : "non configuré"}
        </span>
        <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${stats.smtp_prospecting_configured ? "bg-emerald-100 text-emerald-700" : "bg-neutral-200 text-neutral-600"}`}>
          Email prospection {stats.smtp_prospecting_configured ? "connecté ✓" : "non configuré"}
        </span>
      </div>
      <WeeklyRecap />
    </div>
  );
};

const TABS = [
  { id: "overview", label: "Vue d'ensemble", icon: LayoutDashboard },
  { id: "analytics", label: "Suivi du site", icon: BarChart3 },
  { id: "pipeline", label: "Pipeline", icon: Columns3 },
  { id: "quotes", label: "Devis", icon: FileText },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "mailbox", label: "Boîte mail", icon: Inbox },
  { id: "prospects", label: "Prospects", icon: Users },
  { id: "tiers", label: "Tiers", icon: Building2 },
  { id: "leads", label: "Recherche IA", icon: Globe },
  { id: "campaigns", label: "Campagnes", icon: Mail },
  { id: "autopilot", label: "Prospection auto", icon: Rocket },
  { id: "social", label: "Réseaux sociaux", icon: Share2 },
  { id: "appointments", label: "Agenda", icon: CalendarClock },
  { id: "interventions", label: "Interventions", icon: MapPin },
  { id: "contracts", label: "Clients récurrents", icon: Repeat },
  { id: "absences", label: "Absences", icon: CalendarOff },
  { id: "recruitment", label: "Recrutement", icon: Briefcase },
  { id: "quality", label: "Contrôle qualité", icon: ShieldCheck },
  { id: "site", label: "Site & services", icon: PanelsTopLeft },
  { id: "team", label: "Utilisateurs", icon: KeyRound },
  { id: "dolibarr", label: "Synchro Dolibarr", icon: Cloud },
  { id: "email", label: "Paramètres Email", icon: Settings },
];

const ROLE_LABELS = { master: "Super-admin", major: "Major" };

const Admin = () => {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("overview");
  const [role, setRole] = useState(sessionStorage.getItem("crm_role") || "");

  useEffect(() => {
    const guard = setTimeout(() => { setAuthed(false); setChecking(false); }, 10000);
    authMe()
      .then((r) => { setAuthed(true); setRole(r.data.role || ""); sessionStorage.setItem("crm_role", r.data.role || ""); })
      .catch(() => setAuthed(false))
      .finally(() => { clearTimeout(guard); setChecking(false); });
    return () => clearTimeout(guard);
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <span className="text-neutral-400 text-sm">Chargement…</span>
      </div>
    );
  }

  if (!authed) return <Login onLogin={(r) => { setRole(r || ""); setAuthed(true); }} />;

  const logout = async () => {
    try { await authLogout(); } catch (error) { log.error("Déconnexion :", error); }
    sessionStorage.removeItem("crm_login");
    setAuthed(false);
  };

  const currentUser = sessionStorage.getItem("crm_login") || "utilisateur";
  const isMaster = role === "master";

  if (role === "dolibarr") {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center p-6">
        <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 max-w-md text-center" data-testid="admin-easer-notice">
          <Lock size={28} className="text-brand mx-auto mb-3" />
          <h1 className="font-heading font-bold text-xl text-white mb-2">Espace CRM réservé</h1>
          <p className="text-sm text-neutral-400 mb-5">
            Bonjour <b className="text-neutral-200">{currentUser}</b>, votre compte Dolibarr est un compte <b>intervenant</b> (ni super-admin,
            ni membre du groupe « major »). Le CRM est réservé aux super-admins et aux majors ; votre espace est l'application terrain.
          </p>
          <a href="/app" className="inline-block bg-brand hover:bg-brand-dark text-white font-semibold px-5 py-2.5 rounded-md" data-testid="admin-goto-app">Ouvrir l'espace intervenant</a>
          <button onClick={logout} className="block mx-auto mt-4 text-sm text-neutral-400 hover:text-white">Se déconnecter</button>
        </div>
      </div>
    );
  }
  const roleLabel = ROLE_LABELS[role] || "Intervenant";
  const visibleTabs = TABS.filter((t) => isMaster || !["team", "email"].includes(t.id));
  const activeTab = visibleTabs.some((t) => t.id === tab) ? tab : "overview";

  return (
    <div className="min-h-screen bg-neutral-50 font-body">
      <div className="bg-neutral-950 text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="PTITSERVICES-PRO" className="h-9 w-auto rounded-md" data-testid="admin-logo" />
            <span className="text-sm text-neutral-300">Espace CRM</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:flex items-center gap-1.5 text-sm text-neutral-400" data-testid="admin-role-badge">
              <User size={15} /> {currentUser} · {roleLabel}
            </span>
            <a href="/" className="text-sm text-neutral-400 hover:text-white">Voir le site</a>
            <button onClick={logout} className="flex items-center gap-1.5 text-sm text-neutral-300 hover:text-white">
              <LogOut size={16} /> Déconnexion
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8 grid lg:grid-cols-[220px_1fr] gap-8">
        <aside>
          <nav className="flex lg:flex-col gap-1 overflow-x-auto">
            {visibleTabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === t.id ? "bg-brand text-white" : "text-neutral-600 hover:bg-neutral-100"
                }`}
              >
                <t.icon size={17} /> {t.label}
              </button>
            ))}
          </nav>
        </aside>

        <main className="min-w-0">
          {activeTab === "overview" && <Overview />}
          {activeTab === "analytics" && <AnalyticsPanel />}
          {activeTab === "pipeline" && <PipelinePanel />}
          {activeTab === "quotes" && <QuotesPanel />}
          {activeTab === "messages" && <MessagesPanel />}
          {activeTab === "prospects" && <ProspectsPanel />}
          {activeTab === "leads" && <LeadsPanel />}
          {activeTab === "campaigns" && <CampaignsPanel />}
          {activeTab === "autopilot" && <AutopilotPanel isMaster={isMaster} />}
          {activeTab === "social" && <SocialPanel />}
          {activeTab === "appointments" && <AppointmentsPanel onNavigate={setTab} isMaster={isMaster} />}
          {activeTab === "mailbox" && <MailboxPanel isMaster={isMaster} />}
          {activeTab === "contracts" && <ContractsPanel isMaster={isMaster} />}
          {activeTab === "absences" && <AbsencesPanel isMaster={isMaster} />}
          {activeTab === "recruitment" && <RecruitmentPanel />}
          {activeTab === "tiers" && <TiersPanel onNavigate={setTab} isMaster={isMaster} />}
          {activeTab === "interventions" && <InterventionsPanel isMaster={isMaster} />}
          {activeTab === "site" && <SitePanel isMaster={isMaster} />}
          {activeTab === "team" && isMaster && <TeamPanel />}
          {activeTab === "dolibarr" && <DolibarrSyncPanel isMaster={isMaster} />}
          {activeTab === "quality" && <QualityPanel isMaster={isMaster} />}
          {activeTab === "email" && isMaster && <EmailSettingsPanel />}
        </main>
      </div>
    </div>
  );
};

export default Admin;
